import React, { useRef, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Phone, PhoneOff, Video, VideoOff, Mic, MicOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CallState, CallInvitation } from '@/types/call';

interface SimpleCallInterfaceProps {
  // Call state
  callState: CallState;
  incomingCall: CallInvitation | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  
  // Actions
  onAcceptCall: () => void;
  onRejectCall: () => void;
  onEndCall: () => void;
}

const SimpleCallInterface: React.FC<SimpleCallInterfaceProps> = ({
  callState,
  incomingCall,
  localStream,
  remoteStream,
  onAcceptCall,
  onRejectCall,
  onEndCall
}) => {
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);

  // Setup video streams
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  // Incoming call dialog
  if (incomingCall) {
    return (
      <Dialog open={true} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md glass border-border/30 overflow-hidden" aria-describedby="incoming-call-description">
          <div className="sr-only">
            <h2 id="incoming-call-title">Chiamata in arrivo</h2>
            <p id="incoming-call-description">
              {incomingCall.isVideo ? 'Videochiamata' : 'Chiamata vocale'} in arrivo da {incomingCall.senderName}
            </p>
          </div>
          
          <div className="relative text-center py-8">
            {/* Animated rings around avatar */}
            <div className="relative mx-auto mb-6">
              <div className="absolute inset-0 rounded-full border-4 border-primary/30 animate-ping"></div>
              <div className="absolute inset-2 rounded-full border-2 border-primary/50 animate-ping" style={{ animationDelay: '0.5s' }}></div>
              <Avatar className="h-24 w-24 mx-auto ring-4 ring-primary/20 relative z-10">
                <AvatarImage src={incomingCall.senderAvatar} />
                <AvatarFallback className="text-2xl gradient-primary text-primary-foreground">
                  {incomingCall.senderName[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </div>

            {/* Call Info */}
            <div className="space-y-2 mb-8">
              <h3 className="text-xl font-semibold text-foreground">
                {incomingCall.senderName}
              </h3>
              <p className="text-muted-foreground">
                {incomingCall.isVideo ? 'Videochiamata in arrivo...' : 'Chiamata in arrivo...'}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center space-x-8">
              {/* Reject Button */}
              <Button
                onClick={onRejectCall}
                className={cn(
                  "h-16 w-16 p-0 rounded-full",
                  "bg-destructive hover:bg-destructive/90 text-destructive-foreground",
                  "shadow-lg hover:shadow-destructive/20 transition-all duration-300",
                  "hover:scale-110"
                )}
              >
                <PhoneOff className="w-8 h-8" />
              </Button>

              {/* Accept Button */}
              <Button
                onClick={onAcceptCall}
                className={cn(
                  "h-16 w-16 p-0 rounded-full",
                  "bg-green-500 hover:bg-green-600 text-white",
                  "shadow-lg hover:shadow-green-500/20 transition-all duration-300",
                  "hover:scale-110"
                )}
              >
                {incomingCall.isVideo ? (
                  <Video className="w-8 h-8" />
                ) : (
                  <Phone className="w-8 h-8" />
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Active call interface
  if (callState.isActive) {
    return (
      <Dialog open={true} onOpenChange={() => {}}>
        <DialogContent className="max-w-4xl glass border-border/30 overflow-hidden p-0" aria-describedby="active-call-description">
          <div className="sr-only">
            <h2 id="active-call-title">Chiamata attiva</h2>
            <p id="active-call-description">
              {callState.isVideo ? 'Videochiamata' : 'Chiamata vocale'} con {callState.participantName}
            </p>
          </div>
          
          <div className="relative h-[600px] bg-gradient-to-br from-background/50 to-muted/50 flex flex-col">
            {callState.isVideo ? (
              <>
                {/* Remote Video (main) */}
                <div className="flex-1 relative">
                  {remoteStream ? (
                    <video
                      ref={remoteVideoRef}
                      autoPlay
                      playsInline
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-muted">
                      <div className="text-center">
                        <Avatar className="h-24 w-24 mx-auto mb-4">
                          <AvatarImage src={callState.participantAvatar} />
                          <AvatarFallback className="text-2xl">
                            {callState.participantName?.[0]?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <p className="text-muted-foreground">
                          {callState.isConnecting ? 'Connessione...' : 'In attesa del video'}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Local Video (picture-in-picture) */}
                  {localStream && (
                    <div className="absolute top-4 right-4 w-48 h-36 rounded-lg overflow-hidden border-2 border-white/20 shadow-lg">
                      <video
                        ref={localVideoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* Audio call interface */
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="relative mb-8">
                    <div className="absolute inset-0 rounded-full border-4 border-primary/20 animate-pulse"></div>
                    <Avatar className="h-32 w-32 mx-auto ring-4 ring-primary/10">
                      <AvatarImage src={callState.participantAvatar} />
                      <AvatarFallback className="text-4xl gradient-primary text-primary-foreground">
                        {callState.participantName?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  
                  <h3 className="text-2xl font-semibold text-foreground mb-2">
                    {callState.participantName}
                  </h3>
                  <p className="text-muted-foreground text-lg">
                    {callState.isConnecting ? 'Connessione...' : 'Chiamata in corso'}
                  </p>
                </div>
              </div>
            )}

            {/* Call Controls */}
            <div className="p-6 glass border-t border-border/30">
              <div className="flex justify-center space-x-6">
                {/* Mute Button */}
                <Button
                  variant="outline"
                  size="lg"
                  className="h-14 w-14 p-0 rounded-full border-border/50 hover:bg-muted/50"
                >
                  <Mic className="w-6 h-6" />
                </Button>

                {/* Video Toggle (if video call) */}
                {callState.isVideo && (
                  <Button
                    variant="outline"
                    size="lg"
                    className="h-14 w-14 p-0 rounded-full border-border/50 hover:bg-muted/50"
                  >
                    <Video className="w-6 h-6" />
                  </Button>
                )}

                {/* End Call Button */}
                <Button
                  onClick={onEndCall}
                  className={cn(
                    "h-14 w-14 p-0 rounded-full",
                    "bg-destructive hover:bg-destructive/90 text-destructive-foreground",
                    "shadow-lg hover:shadow-destructive/20 transition-all duration-300"
                  )}
                >
                  <PhoneOff className="w-6 h-6" />
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return null;
};

export default SimpleCallInterface;